import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'

import { H3, P } from '@mxenabled/mxui'
import { AccountBalance, CalendarMonth, CreditCard, Payments } from '@mxenabled/mx-icons'

import ConnectionsDrawer from '../../accounts/components/ConnectionsDrawer'
import {
  Drawer,
  useAccountStore,
  useGlobalCopyStore,
  useRecurringTransactionsStore,
  useUserStore,
} from '../../common'
import { Frequency, RepeatingType } from '../../core'
import { RecurringSettings } from '../../recurringtransactions'

import { buildProfileItem } from '../util/finstrongUtils'

import { ConnectMoreAccountsCard } from './ConnectMoreAccountsCard'
import ManageCreditScore from './ManageCreditScore'
import ManageDOB from './ManageDOB'
import ProfileList, { Action } from './ProfileList'
import ProfileProgressBar from './ProfileProgressBar'
import { buildDynamicCopy } from '../../core/utils/Localization'

interface ProfileDrawerProps {
  isOpen: boolean
  onClose: () => void
}

const ProfileDrawer = ({ isOpen, onClose }: ProfileDrawerProps) => {
  const { visibleFinstrongAccounts: accounts } = useAccountStore()
  const {
    accounts: accountsCopy,
    common: commonCopy,
    finstrong: finstrongCopy,
    recurring: recurringCopy,
  } = useGlobalCopyStore()
  const { repeatingTransactions } = useRecurringTransactionsStore()
  const { user } = useUserStore()

  const totalIncomeAmount = repeatingTransactions.reduce((sum, rt) => {
    const { repeating_transaction_type, transactions, recurrence_type, amount } = rt

    const isIncome =
      repeating_transaction_type === RepeatingType.Income &&
      ((transactions && transactions.length > 0) || recurrence_type === Frequency.EveryYear)

    return isIncome ? sum + amount : sum
  }, 0)

  const [action, setAction] = React.useState<Action | null>(null)

  const profileItemsData = [
    {
      dynamicCopy: finstrongCopy.add_profile_item,
      fallbackLabel: finstrongCopy.monthly_income,
      icon: <Payments />,
      key: Action.MonthlyIncome,
      label: finstrongCopy.monthly_income,
      value: totalIncomeAmount,
    },
    {
      dynamicCopy: finstrongCopy.add_profile_item,
      fallbackLabel: finstrongCopy.date_of_birth,
      icon: <CalendarMonth />,
      key: Action.DateOfBirth,
      label: finstrongCopy.date_of_birth,
      value: user.birthday,
    },
    {
      dynamicCopy: finstrongCopy.add_profile_item,
      fallbackLabel: finstrongCopy.credit_score,
      icon: <CreditCard />,
      key: Action.CreditScore,
      label: finstrongCopy.credit_score,
      value: user.credit_score,
    },
    {
      fallbackLabel: finstrongCopy.connect_your_accounts,
      icon: <AccountBalance />,
      key: Action.Connections,
      label: finstrongCopy.connections,
      value:
        accounts.length > 0
          ? (buildDynamicCopy(
              finstrongCopy.connected_account,
              String(accounts.length),
              accounts.length > 1 ? finstrongCopy.accounts : finstrongCopy.account,
            ) as string)
          : undefined,
    },
  ]

  const list = profileItemsData.map((item) => buildProfileItem(item))

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={finstrongCopy.financial_profile}>
      <Stack sx={{ bgcolor: 'background.default', height: '100%' }}>
        <Stack sx={{ gap: 20, p: 24 }}>
          <Stack sx={{ gap: 8 }}>
            <H3>{finstrongCopy.your_financial_profile}</H3>
            <P>{finstrongCopy.financial_profile_description}</P>
          </Stack>
          <ProfileProgressBar
            completeCopy={finstrongCopy.complete}
            value={list.reduce((acc, item) => acc + (item.isComplete ? 1 : 0), 0)}
          />
        </Stack>
        <ProfileList list={list} onClickItem={setAction} />
        <Box sx={{ p: 24 }}>
          <ConnectMoreAccountsCard
            description={
              finstrongCopy.connect_more_accounts_to_view_more_accurate_financial_picture
            }
            drawerType="connectDrawer"
            isDashboard={true}
            title={finstrongCopy.dont_see_all_your_accounts}
            titleDrawer={commonCopy.add_accounts}
          />
        </Box>
      </Stack>

      <Drawer
        isOpen={action === Action.MonthlyIncome}
        onClose={() => setAction(null)}
        shouldShowHeaderBorders={false}
        title={recurringCopy.action_title}
      >
        <RecurringSettings />
      </Drawer>
      <ManageDOB isOpen={action === Action.DateOfBirth} onClose={() => setAction(null)} />
      <ManageCreditScore isOpen={action === Action.CreditScore} onClose={() => setAction(null)} />
      <ConnectionsDrawer
        onClose={() => setAction(null)}
        showConnectionsWidget={action === Action.Connections}
        title={accountsCopy.manage_connections}
      />
    </Drawer>
  )
}
export default observer(ProfileDrawer)
